import {
  Box,
  Tooltip,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  CircularProgress,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { IconButton } from "@mui/material";
import {
  LayerGroup,
  MapContainer,
  ScaleControl,
  useMapEvents,
} from "react-leaflet";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { MapTools } from "./MapTools";
import { GroupEraseLayer } from "./tools/GroupEraseLayer";
import { SimplifyPreview } from "./tools/SimplifyPreview";
import ExoAlert from "../exo/ExoAlert";
import { createElement, useEffect, useMemo, useRef, useState } from "react";
import { tokens } from "../../theme/theme";
import Alert from "../Alert";
import ElementTypes from "./helperFunctions/elementTypes";
import { offsetCoincidentNodes } from "./helperFunctions/offsetCoincidentNodes";
import { getElevationRange } from "./helperFunctions/elevationColor";
import { getMetricRange } from "./helperFunctions/edgeMetricColor";
import { ElevationLegend } from "./mapUtility/ElevationLegend";
import { MetricLegend } from "./mapUtility/MetricLegend";
import { ResultSummaryPanel } from "./mapUtility/ResultSummaryPanel";
import { isNodeInsidePolygon } from "./helperFunctions/isNodeInsidePolygon";
import { MapElementPropertyManager } from "./MapElementPropertyManager";
import { findMapElementById } from "./helperFunctions/findMapElementById";
import { MapUtility } from "./MapUtility";
import { MapActions } from "./MapActions";
import { ElevationProfileDialog } from "./mapUtility/ElevationProfileDialog";
import {
  findShortestPath,
  buildProfileData,
} from "./helperFunctions/elevationProfile";
import { calculateCenter } from "./helperFunctions/calculateCenter";
import { calculateZoom } from "./helperFunctions/calculateZoom";
import {
  createLeafletSetViewOptions,
  LEAFLET_MAP_OPTIONS,
} from "./leafletMapOptions";
import "./customStyles.scss";

const demoTools = [
  "clear",
  "delete",
  "projectArea",
  "center",
  "node",
  "edge",
];

const demoActions = [
  {
    label: "default",
    color: "primary",
    variant: "contained",
    onClick: (e) => console.log(e),
  },
  {
    label: "warning",
    color: "warning",
    variant: "contained",
    onClick: (e) => console.log(e),
  },
  {
    label: "warning",
    color: "warning",
    variant: "outlined",
    onClick: (e) => console.log(e),
  },
  {
    label: "error",
    color: "error",
    variant: "contained",
    onClick: (e) => console.log(e),
  },
  {
    label: "error",
    color: "error",
    variant: "outlined",
    onClick: (e) => console.log(e),
  },
  {
    label: "success",
    color: "success",
    variant: "contained",
    onClick: (e) => console.log(e),
  },
  {
    label: "success",
    color: "success",
    variant: "outlined",
    onClick: (e) => console.log(e),
  },
];

// Reports the current zoom level to the parent so the pixel-based node offset
// can recompute and keep a constant visual gap across zoom levels.
const MapZoomWatcher = ({ onZoom }) => {
  const map = useMapEvents({
    zoomend: () => onZoom(map.getZoom()),
  });
  return null;
};

const MapGridDesigner = ({
  startCenter,
  startZoom,
  startElements,
  startProjectAreas,
  disconnectedNodeIds = [],
  overDepthNodeIds = [],
  onEdit,
  tools = demoTools,
  actions = demoActions,
  onSave,
  onClose,
  preview,
  noResultMode,
  resultElements,
  resultVersion = 0,
  customTools,
  maxProjectAreas = 1,
  debug,
  takeMeBack,
  forceSaveOnActions,
  onSimplify,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const mapContainerRef = useRef(null);

  // center of map
  var center =
    startCenter || startProjectAreas
      ? calculateCenter(startProjectAreas)
      : [50, 10];

  // zoom
  var zoom =
    startZoom || startProjectAreas ? calculateZoom(startProjectAreas) : 6;

  useEffect(() => {
    if (!mapContainerRef || !mapContainerRef.current) return;
    var newCenter = center;
    var newZoom = zoom;
    if (!startCenter && startProjectAreas) {
      newCenter = calculateCenter(startProjectAreas);
    }
    if (!startZoom && startProjectAreas) {
      newZoom = calculateZoom(startProjectAreas);
    }
    mapContainerRef.current.setView(
      newCenter,
      newZoom,
      createLeafletSetViewOptions()
    );
  }, [startProjectAreas, mapContainerRef]);

  // alert
  const [alert, setAlert] = useState({
    type: "warning",
    message: "",
    open: false,
  });

  const showAlert = (message, type) => {
    setAlert({ alertText: message, alertType: type, alertOpen: true });
  };

  // modes
  const [currentMode, setCurrentMode] = useState("edit"); // current mode of interaction like "view", "edit", "newNode", "newEdge", "newPolygon"

  // elements
  const [mapElements, setMapElements] = useState([]);
  // history of previous mapElements snapshots for step-by-step undo
  const [history, setHistory] = useState([]);
  // snapshots that were undone, available to redo (cleared on a new edit)
  const [future, setFuture] = useState([]);
  const MAX_HISTORY = 50;
  const [projectAreas, setProjectAreas] = useState([]);
  const [selectedElement, setSelectedElement] = useState({});
  const [elementSelection, setElementSelection] = useState([]);
  // set when the map was cleared; a clear empties mapElements so the normal
  // "unsaved elements" check can't detect it. Kept until the next save/reload
  // so opening the actions menu still forces a save after a clear.
  const [clearedSinceSave, setClearedSinceSave] = useState(false);

  // Basegrid vs. result view: the basegrid (mapElements) is what the user
  // edits; the optimization result (resultElements) is a frozen, separate
  // dataset. The result view renders the result read-only — basegrid edits
  // never touch it, only a new optimization replaces it.
  const hasResult = Array.isArray(resultElements) && resultElements.length > 0;
  const isResultDisplayed = currentMode === "view" && hasResult;
  const displayedElements = isResultDisplayed ? resultElements : mapElements;

  // a freshly computed optimization result switches to the result view
  useEffect(() => {
    if (resultVersion > 0) setCurrentMode("view");
  }, [resultVersion]);

  // "Visualize Elevation" view: color nodes by their elevation.
  const [showElevation, setShowElevation] = useState(false);
  // which elevation drives the coloring: "ground" (terrain, from the props) or
  // "invert" (calculated, only available in the result view). The basegrid
  // view always uses ground elevation.
  const [elevationMetric, setElevationMetric] = useState("ground");
  // Result view: how the flow direction is visualized on the edges. Cycled via
  // the flow-direction button:
  //   "animated"           = flowing dash animation
  //   "arrows"             = one static arrow per edge (start -> end)
  //   "none" (default)     = plain edges, no direction hint
  const [flowDisplay, setFlowDisplay] = useState("none");
  // Base size (px) of the edge direction arrows, adjustable via the +/- buttons
  // next to the flow-arrow toggle. Also drives the always-on pressure-pipe
  // arrows, which are drawn a bit larger on top of this.
  const [arrowSize, setArrowSize] = useState(10);
  // Result view: which calc value drives the edge thickness.
  // "diameter" | "first_year_pipe_flow" | "final_year_pipe_flow"
  const [edgeMetric, setEdgeMetric] = useState("diameter");
  // Result view: toggles the floating Kennzahlen (metrics) summary panel.
  const [showMetrics, setShowMetrics] = useState(false);
  // Result view: pulls nodes that sit on the exact same coordinate (base-grid
  // edge cuts) slightly apart so the cut becomes visible. Purely visual.
  const [showOffset, setShowOffset] = useState(false);
  // Bumped on every map zoom so the pixel-based offset recomputes and keeps a
  // constant visual gap across zoom levels.
  const [zoomLevel, setZoomLevel] = useState(zoom);

  // When the result-view "Offset" toggle is on, render a copy of the result
  // where coincident nodes (base-grid edge cuts) are nudged apart. Only moved
  // nodes get new objects, so unmoved elements keep their reference. zoomLevel
  // is a dependency because the offset is computed in pixel space.
  const renderedElements = useMemo(
    () =>
      showOffset && isResultDisplayed && mapContainerRef.current
        ? offsetCoincidentNodes(displayedElements, mapContainerRef.current)
        : displayedElements,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showOffset, isResultDisplayed, displayedElements, zoomLevel]
  );
  // Elevation profile: after activating via the toolbar button the next two
  // node clicks pick start and end; profileData opens the chart dialog.
  const [profileActive, setProfileActive] = useState(false);
  const [profileNodes, setProfileNodes] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const effectiveElevationMetric =
    currentMode === "view" ? elevationMetric : "ground";
  const elevationRange = useMemo(
    () => getElevationRange(displayedElements, effectiveElevationMetric),
    [displayedElements, effectiveElevationMetric]
  );

  useEffect(() => {
    if (Array.isArray(startElements)) { // <-- Array.isArray for a safe check
      setMapElements(startElements);
      setHistory([]); // a fresh load starts a new undo history
      setFuture([]); // and clears any pending redo
      // a load/sync from the parent (also happens right after a save) is a
      // clean, saved baseline again
      setClearedSinceSave(false);
    }
  }, [startElements]);

  useEffect(() => {
    setProjectAreas(startProjectAreas || []);
  }, [startProjectAreas]);

  // pressing ESC aborts the current editing action: it leaves any active tool
  // (stops placing new nodes/edges, cancels an edge that is being drawn) and
  // clears the current selection. An in-progress node drag is cancelled inside
  // the Node component itself.
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key !== "Escape") return;
      // don't interfere while typing in a form field
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable)
        return;
      setCurrentMode((mode) => (mode === "view" ? mode : "edit"));
      setSelectedElement({});
      setElementSelection([]);
      // also leave the elevation-profile picking mode
      setProfileActive(false);
      setProfileNodes([]);
      setProfileData(null);
      // and abort a pending simplify selection
      setSimplifySelection(null);
      setSimplifyPreviews(null);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Ctrl/Cmd+Z undoes, Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y redoes. Re-subscribes on
  // history/future change so the handler always sees the current stacks.
  useEffect(() => {
    function handleUndoKey(e) {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      const isUndo = !e.shiftKey && key === "z";
      const isRedo = (e.shiftKey && key === "z") || key === "y";
      if (!isUndo && !isRedo) return;
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable)
        return;
      e.preventDefault();
      if (isUndo) handleUndo();
      else handleRedo();
    }
    document.addEventListener("keydown", handleUndoKey);
    return () => document.removeEventListener("keydown", handleUndoKey);
  }, [history, future]);

  function handleModeChange() {
    // the result view needs an optimization result — until one exists the
    // toggle towards it is disabled (button is grayed out)
    if (currentMode !== "view" && !hasResult) return;
    setCurrentMode(currentMode === "view" ? "edit" : "view");
  }

  // wraps setMapElements for all user-driven edits (tools, dragging,
  // property changes) so the parent can react to map edits, e.g. to clear the
  // "disconnected nodes" highlighting. The initial load / parent sync uses the
  // raw setMapElements and therefore does not trigger onEdit.
  function handleEditElements(value) {
    if (onEdit) onEdit();
    // record the state before this edit so it can be undone step by step
    setHistory((h) => [...h, mapElements].slice(-MAX_HISTORY));
    // a fresh edit invalidates anything that was previously undone
    setFuture([]);
    setMapElements(value);
  }

  // keep the inspection panel open on the selected element when it still exists
  // (e.g. undo/redo of a property change shows the value); only clear the
  // selection when that element is gone (e.g. undoing an add / redoing a delete).
  function keepOrClearSelection(nextElements) {
    if (
      selectedElement.id &&
      !nextElements.some((element) => element.id === selectedElement.id)
    ) {
      setSelectedElement({});
    }
  }

  // step-by-step undo: restore the most recent snapshot
  function handleUndo() {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setHistory(history.slice(0, -1));
    // remember the current state so it can be redone
    setFuture((f) => [...f, mapElements].slice(-MAX_HISTORY));
    setMapElements(previous);
    keepOrClearSelection(previous);
    if (onEdit) onEdit();
  }

  // step-by-step redo: re-apply the most recently undone snapshot
  function handleRedo() {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    setFuture(future.slice(0, -1));
    // pushing the current state back onto history keeps undo working
    setHistory((h) => [...h, mapElements].slice(-MAX_HISTORY));
    setMapElements(next);
    keepOrClearSelection(next);
    if (onEdit) onEdit();
  }

  // Group Erase: elements whose ids are staged for deletion after the user
  // drew a selection shape and before they confirm.
  const [pendingGroupDelete, setPendingGroupDelete] = useState(null);
  // selection shape for Group Erase: "rectangle" | "polygon"
  const [groupEraseShape, setGroupEraseShape] = useState("rectangle");

  // find every element inside the drawn selection ring (rectangle or polygon,
  // both delivered as a list of [lat,lng] positions). Nodes are matched by
  // their position; edges are removed when either endpoint is inside (so no
  // dangling edges remain); areas are removed when the whole polygon is enclosed.
  function handleGroupEraseComplete(positions) {
    const isInside = (pos) =>
      Array.isArray(pos) && pos.length === 2 && isNodeInsidePolygon(pos, positions);

    const nodeIds = new Set();
    mapElements.forEach((el) => {
      if (el.type === "node" && isInside(el.position)) nodeIds.add(el.id);
    });

    const idsToDelete = new Set(nodeIds);
    mapElements.forEach((el) => {
      if (
        el.type === "edge" &&
        (nodeIds.has(el.startNodeId) || nodeIds.has(el.endNodeId))
      ) {
        idsToDelete.add(el.id);
      }
      if (
        el.type === "area" &&
        Array.isArray(el.positions) &&
        el.positions.length > 0 &&
        el.positions.every((p) => isInside(p))
      ) {
        idsToDelete.add(el.id);
      }
    });

    if (idsToDelete.size === 0) {
      showAlert(t("No elements inside the selection."), "warning");
      return;
    }
    setPendingGroupDelete([...idsToDelete]);
  }

  function confirmGroupDelete() {
    if (!pendingGroupDelete) return;
    const remove = new Set(pendingGroupDelete);
    handleEditElements(mapElements.filter((el) => !remove.has(el.id)));
    if (selectedElement.id && remove.has(selectedElement.id))
      setSelectedElement({});
    setPendingGroupDelete(null);
  }

  // "Delete all not connected elements": only offered right after an
  // optimization flagged disconnected nodes (rendered red). Removes every
  // disconnected node plus the edges attached to it. Runs through
  // handleEditElements -> undoable, and clears the red highlighting (onEdit),
  // which also hides the button again.
  const [confirmDeleteDisconnected, setConfirmDeleteDisconnected] =
    useState(false);

  function deleteDisconnectedElements() {
    const disconnectedNodeIdSet = new Set();
    mapElements.forEach((el) => {
      if (el.type === "node" && checkIfDisconnected(el))
        disconnectedNodeIdSet.add(el.id);
    });
    const remove = new Set(disconnectedNodeIdSet);
    mapElements.forEach((el) => {
      if (
        el.type === "edge" &&
        (disconnectedNodeIdSet.has(el.startNodeId) ||
          disconnectedNodeIdSet.has(el.endNodeId))
      ) {
        remove.add(el.id);
      }
    });
    handleEditElements(mapElements.filter((el) => !remove.has(el.id)));
    if (selectedElement.id && remove.has(selectedElement.id))
      setSelectedElement({});
    setConfirmDeleteDisconnected(false);
  }

  // Simplify: the tool button switches to the "simplify" mode, in which the
  // user draws a rectangle around the part of the network to simplify. The
  // finished selection asks the parent (which owns the API access) for ALL
  // three simplification variants at once; the dialog then shows a small map
  // preview per degree and clicking one applies that precomputed result
  // through handleEditElements, so a simplification is undoable like any
  // other edit.
  const [simplifySelection, setSimplifySelection] = useState(null);
  // { "1": elements, "2": elements, "3": elements } once the backend answered
  const [simplifyPreviews, setSimplifyPreviews] = useState(null);
  // degree whose preview is shown enlarged (magnifier click); null = none
  const [enlargedDegree, setEnlargedDegree] = useState(null);
  // selection shape for simplify: "rectangle" | "polygon"
  const [simplifyShape, setSimplifyShape] = useState("rectangle");

  // total pipe length (m) over all edges, for the before/after comparison on
  // the preview cards
  function totalEdgeLength(elements) {
    let sum = 0;
    (elements || []).forEach((el) => {
      if (el.type !== "edge") return;
      const lengthProp = (el.props || []).find(
        (prop) => prop.key === "length" || prop.label === "length"
      );
      const value = Number(lengthProp && lengthProp.value);
      if (!isNaN(value)) sum += value;
    });
    return sum;
  }

  function formatLength(meters) {
    return meters >= 1000
      ? `${(meters / 1000).toFixed(2)} km`
      : `${Math.round(meters)} m`;
  }

  function handleSimplifyAreaComplete(positions) {
    const hasNodesInside = mapElements.some(
      (el) =>
        el.type === "node" && isNodeInsidePolygon(el.position, positions)
    );
    if (!hasNodesInside) {
      showAlert(t("No nodes inside the selection."), "warning");
      return;
    }
    if (!onSimplify) return;
    setSimplifyPreviews(null);
    setSimplifySelection(positions);
    onSimplify(mapElements, positions, (results) => {
      // null signals a failed request (the parent already showed the error)
      if (!results) {
        cancelSimplify();
        return;
      }
      setSimplifyPreviews(results);
    });
  }

  function cancelSimplify() {
    setSimplifySelection(null);
    setSimplifyPreviews(null);
    setEnlargedDegree(null);
    setCurrentMode("edit");
  }

  function handleSimplifyConfirm(degree) {
    const newElements = simplifyPreviews && simplifyPreviews[degree];
    const nodeCountBefore = mapElements.filter(
      (el) => el.type === "node"
    ).length;
    cancelSimplify();
    if (!Array.isArray(newElements)) return;
    handleEditElements(newElements);
    keepOrClearSelection(newElements);
    const removed =
      nodeCountBefore - newElements.filter((el) => el.type === "node").length;
    if (removed > 0) {
      showAlert(
        t("Simplification removed {{n}} node(s).", { n: removed }),
        "success"
      );
    } else {
      showAlert(
        t("No nodes could be removed at this simplification level."),
        "info"
      );
    }
  }

  const simplifyDegrees = [
    {
      degree: 1,
      label: t("Slight"),
      description: t(
        "Removes only nodes that lie almost exactly on a straight line."
      ),
    },
    {
      degree: 2,
      label: t("Moderate"),
      description: t(
        "Straightens pipe runs while keeping the general routing."
      ),
    },
    {
      degree: 3,
      label: t("Strong"),
      description: t(
        "Keeps only junctions and special nodes (outlets, lift stations, ...)."
      ),
    },
  ];

  // Elevation profile: toolbar button toggles the picking mode, the next two
  // node clicks pick start and end, then the chart dialog opens.
  function toggleElevationProfile() {
    if (profileActive) return exitElevationProfile();
    setProfileActive(true);
    // leave any active drawing tool so node clicks are not consumed by it
    setCurrentMode((mode) => (mode === "view" ? mode : "edit"));
    setSelectedElement({});
    showAlert(t("Select the start node"), "info");
  }

  function exitElevationProfile() {
    setProfileActive(false);
    setProfileNodes([]);
    setProfileData(null);
    setElementSelection([]);
  }

  function handleProfileNodeClick(element) {
    if (element.type !== "node") return;
    if (profileNodes.length === 0) {
      setProfileNodes([element]);
      setElementSelection([element]); // highlight the chosen start node
      showAlert(t("Select the end node"), "info");
    } else if (profileNodes[0].id === element.id) {
      showAlert(t("Please select two different nodes."), "warning");
    } else {
      // in the result view the profile runs over the optimization result
      // (which carries the invert elevations)
      const path = findShortestPath(
        displayedElements,
        profileNodes[0].id,
        element.id
      );
      if (!path) {
        showAlert(t("No path found between the selected nodes."), "warning");
      } else {
        setElementSelection([profileNodes[0], element]);
        setProfileData({
          ...buildProfileData(path),
          start: profileNodes[0],
          end: element,
        });
      }
    }
  }

  // select and selection
  function handleMapElementClick(element) {
    if (profileActive) {
      handleProfileNodeClick(element);
      return;
    }
    if (currentMode === "selection") {
      const existingElement = elementSelection.find(
        (obj) => obj.id === element.id
      );
      if (existingElement) {
        const newElements = elementSelection.filter(
          (obj) => obj.id !== element.id
        );
        setElementSelection(newElements);
      } else {
        setElementSelection([...elementSelection, element]);
      }
    } else {
      setSelectedElement({ ...element });
    }
  }

  // the integrity check may report node ids (uuid) or node names, as numbers
  // or strings -> normalize everything to strings for a robust comparison
  const disconnectedKeys = disconnectedNodeIds.map(String);
  function checkIfDisconnected(element) {
    return (
      disconnectedKeys.includes(String(element.id)) ||
      disconnectedKeys.includes(String(element.name))
    );
  }

  // result nodes whose buried depth exceeds the configured maximum (set after
  // an optimization); Set lookup so the per-node check stays O(1)
  const overDepthKeys = new Set(overDepthNodeIds.map(String));
  function checkIfOverDepth(element) {
    return overDepthKeys.has(String(element.id));
  }

  function checkIfInSelection(element) {
    const isInSelection = elementSelection.findIndex(
      (obj) => obj.id === element.id
    );
    return isInSelection >= 0;
  }

  function handleProjectAreaCheck(nodePosition) {
    var isValid = false;
    projectAreas.forEach((area) => {
      if (isNodeInsidePolygon(nodePosition, area.positions)) isValid = true;
    });
    return isValid;
  }

  function handleSave() {
    // a save persists the current (possibly cleared) state, so the map is no
    // longer "dirty from a clear". Reset here rather than relying on the parent
    // re-sync: an empty map round-trips from the backend as null (not []), so
    // the startElements effect below would never fire to clear the flag.
    setClearedSinceSave(false);
    onSave(mapElements, projectAreas);
  }

  // filter
  const [filter, setFilter] = useState(null);

  // calc weight multipier for edges
  // Maps the selected calc value (diameter or a pipe flow) linearly to an edge
  // thickness between minWeight and maxWeight, based on the value range across
  // all edges. Returns {a, b} so weight = value * a + b.
  function calcWeightMultiplierForEdges(
    elements,
    minWeight,
    maxWeight,
    label = "diameter"
  ) {
    var minV = null;
    var maxV = null;
    elements.forEach((element) => {
      if (element.type === "edge" && element.calc) {
        element.calc.forEach((prop) => {
          if (prop.label === label && prop.value != null && !isNaN(prop.value)) {
            const v = Number(prop.value);
            if (minV === null || v < minV) minV = v;
            if (maxV === null || v > maxV) maxV = v;
          }
        });
      }
    });

    // no data yet -> weight becomes 0 and Edge falls back to the style weight
    if (minV === null) return { a: 0, b: 0 };
    // all equal -> uniform medium thickness (avoid division by zero)
    if (maxV === minV) return { a: 0, b: (minWeight + maxWeight) / 2 };

    const a = (maxWeight - minWeight) / (maxV - minV);
    const b = minWeight - a * minV;

    return { a, b };
  }

  // The color/thickness scale is anchored to a single metric so that first-year
  // and final-year flow share the same scale and stay comparable. Final year
  // determines the maximum (its flows are >= the first year's).
  const scaleMetric =
    edgeMetric === "first_year_pipe_flow" ||
    edgeMetric === "final_year_pipe_flow"
      ? "final_year_pipe_flow"
      : edgeMetric;

  // recompute whenever the elements or the scale metric change
  const weightSettings = useMemo(
    () => calcWeightMultiplierForEdges(displayedElements, 2, 10, scaleMetric),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayedElements, scaleMetric]
  );

  // value range of the scale metric, used to color edges by a gradient
  const metricRange = useMemo(
    () => getMetricRange(displayedElements, scaleMetric),
    [displayedElements, scaleMetric]
  );

  return (
    <Box
      className="flex absolute top-0 left-0 w-full h-full overflow-hidden"
      sx={{
        "& .leaflet-control-attribution.leaflet-control": {
          display: "none",
        },
        "& .leaflet-control-container": {
          display: "none",
        },
        "& .leaflet-tooltip": {
          backgroundColor: "rgba(255,255,255,0.5)",
          border: "none",
          boxShadow: "none",
          padding: "4px 8px",
          backdropFilter: "blur(8px)",
          boxShadow: "0px 3px 5px rgba(0,0,0,0.2)",
          "&::before": {
            borderBottomColor: "rgba(255,255,255)",
          },
        },
      }}
    >
      {debug && (
        <Box
          className="absolute flex flex-col gap-2 p-2 top-0 left-0"
          sx={{ zIndex: 999 }}
        >
          <Button
            variant="contained"
            color="warning"
            onClick={() => console.log(mapElements)}
          >
            LOG MapElements
          </Button>
        </Box>
      )}
      <MapElementPropertyManager
        show={!preview || preview.utility}
        selectedElement={
          (selectedElement.id &&
            findMapElementById(displayedElements, selectedElement.id)) ||
          {}
        }
        setSelectedElement={setSelectedElement}
        mapElements={displayedElements}
        setMapElements={isResultDisplayed ? () => {} : handleEditElements}
        disabled={currentMode === "view"}
        setCurrentMode={setCurrentMode}
        elementSelection={elementSelection}
        setElementSelection={setElementSelection}
        currentMode={currentMode}
        showAlert={showAlert}
      />
      <Tooltip
        title={
          currentMode === "view"
            ? t("Switch to Basegrid View")
            : hasResult
            ? t("Switch to Result View")
            : t("Optimize first to see the results")
        }
        placement="left"
      >
        <Box
          onClick={handleModeChange}
          className="absolute top-2 right-2 flex justify-center items-center aspect-square rounded-lg w-16 z-50 shadow-lg"
          sx={{
            // result view is active -> tint the button with the icon's brand
            // green so the active view is visually distinguishable
            bgcolor:
              currentMode === "view"
                ? "rgba(110, 185, 110, 0.18)"
                : colors.contrast[100],
            border:
              currentMode === "view"
                ? "2px solid #6eb96e"
                : "2px solid transparent",
            display:
              (!preview || preview.views) && !noResultMode ? "flex" : "none",
            // no result yet -> the switch to the result view is disabled
            opacity: currentMode !== "view" && !hasResult ? 0.5 : 1,
            cursor:
              currentMode !== "view" && !hasResult ? "not-allowed" : "pointer",
          }}
        >
          {currentMode === "view" ? (
            <img src="/assets/svg/result-view.svg" alt={t("Result View")} />
          ) : (
            <img src="/assets/svg/design-view.svg" alt={t("Basegrid View")} />
          )}
        </Box>
      </Tooltip>

      <MapContainer
        ref={mapContainerRef}
        // render nodes (CircleMarker) and edges (Polyline) onto a single canvas
        // instead of one SVG element per feature -> scales to large networks.
        // Animated-flow edges opt back into the SVG renderer in Edge.jsx.
        preferCanvas={true}
        doubleClickZoom={false}
        {...LEAFLET_MAP_OPTIONS}
        center={center}
        zoom={zoom}
        maxZoom={21}
        style={{ height: "100%", width: "100%", zIndex: "0" }}
      >
        <ScaleControl position="bottomleft" imperial={false} metric={true} />

        <MapZoomWatcher onZoom={setZoomLevel} />

        <MapUtility
          setFilter={setFilter}
          filter={filter}
          center={center}
          preview={preview}
          hasElevation={Boolean(elevationRange)}
          showElevation={showElevation}
          setShowElevation={setShowElevation}
          elevationMetric={effectiveElevationMetric}
          setElevationMetric={setElevationMetric}
          onUndo={handleUndo}
          canUndo={history.length > 0}
          onRedo={handleRedo}
          canRedo={future.length > 0}
          flowDisplay={flowDisplay}
          setFlowDisplay={setFlowDisplay}
          arrowSize={arrowSize}
          setArrowSize={setArrowSize}
          isResultView={currentMode === "view"}
          edgeMetric={edgeMetric}
          setEdgeMetric={setEdgeMetric}
          showMetrics={showMetrics}
          setShowMetrics={setShowMetrics}
          showOffset={showOffset}
          setShowOffset={setShowOffset}
          profileActive={profileActive}
          onToggleProfile={toggleElevationProfile}
        />

        <MapActions
          actions={actions}
          onClose={onClose}
          mapElements={mapElements}
          projectAreas={projectAreas}
          onSave={onSave ? handleSave : null}
          takeMeBack={takeMeBack}
          forceSaveOnActions={forceSaveOnActions}
          clearedSinceSave={clearedSinceSave}
        />

        {/* Only shown right after an optimization flagged disconnected elements
            (red nodes). Sits above the Save/Actions bar (bottom-right). Any edit
            clears the red highlighting, which removes this button again. */}
        {!preview &&
          !isResultDisplayed &&
          disconnectedNodeIds.length > 0 && (
            <Box
              className="bottom-16 right-8 absolute flex gap-3"
              sx={{ zIndex: "99999" }}
            >
              <Button
                onClick={() => setConfirmDeleteDisconnected(true)}
                variant="contained"
                color="error"
                startIcon={<LinkOffIcon />}
              >
                {t("Delete all not connected elements")}
              </Button>
            </Box>
          )}

        <MapTools
          show={
            currentMode !== "view" &&
            (!preview || preview.utility) &&
            currentMode !== "selection"
          }
          tools={tools}
          onAlert={showAlert}
          selectedElement={selectedElement}
          mapElements={mapElements}
          setMapElements={handleEditElements}
          currentMode={currentMode}
          setCurrentMode={setCurrentMode}
          projectAreas={projectAreas}
          setProjectAreas={setProjectAreas}
          onProjectAreaCheck={handleProjectAreaCheck}
          setSelectedElement={setSelectedElement}
          customTools={customTools}
          maxProjectAreas={maxProjectAreas}
          groupEraseShape={groupEraseShape}
          setGroupEraseShape={setGroupEraseShape}
          onClear={() => setClearedSinceSave(true)}
          simplifyShape={simplifyShape}
          setSimplifyShape={setSimplifyShape}
        />

        <GroupEraseLayer
          active={currentMode === "groupDelete"}
          shape={groupEraseShape}
          onComplete={handleGroupEraseComplete}
        />

        {/* selection shape (rectangle or polygon) for the simplify flow (blue
            instead of the erase red); drawing is paused while the degree
            dialog is open */}
        <GroupEraseLayer
          active={currentMode === "simplify" && !simplifySelection}
          shape={simplifyShape}
          color="#1976d2"
          onComplete={handleSimplifyAreaComplete}
        />

        {displayedElements && (
          <>
            {(!preview || preview.projectAreas) && (
              <LayerGroup>
                {projectAreas &&
                  createElement(ElementTypes["projectArea"], {
                    elements: projectAreas,
                    key: "projectAreas",
                    currentMode,
                    projectAreas,
                    setProjectAreas,
                    selectedElement,
                    onSelect: handleMapElementClick,
                    onProjectAreaCheck: handleProjectAreaCheck,
                  })}
              </LayerGroup>
            )}
            {false && (!preview || preview.area) && (
              <LayerGroup>
                {(filter === null || filter.area) &&
                  mapElements.map((element, index) => {
                    if (element.type !== "area") return;
                    return createElement(ElementTypes[element.type], {
                      element: element,
                      key: element.id,
                      currentMode,
                      mapElements,
                      setMapElements: handleEditElements,
                      selectedElement,
                      onSelect: handleMapElementClick,
                      onProjectAreaCheck: handleProjectAreaCheck,
                      index,
                    });
                  })}
              </LayerGroup>
            )}
            {(!preview || preview.edge) && (
              <LayerGroup>
                {(filter === null || filter.edge) &&
                  renderedElements.map((element, index) => {
                    if (element.type !== "edge") return;
                    return createElement(ElementTypes[element.type], {
                      element: element,
                      key: element.id,
                      currentMode,
                      // the result view renders the frozen result: children get
                      // it as their element list and must never write it back
                      // into the editable basegrid state
                      mapElements: renderedElements,
                      setMapElements: isResultDisplayed
                        ? () => {}
                        : handleEditElements,
                      // derived updates (edge length) skip the undo history
                      updateMapElements: isResultDisplayed
                        ? () => {}
                        : setMapElements,
                      selectedElement,
                      onSelect: handleMapElementClick,
                      onProjectAreaCheck: handleProjectAreaCheck,
                      weightSettings,
                      // flow visualization is a result-view feature (the toggle
                      // only shows there); pressure-pipe arrows stay independent
                      flowDisplay: currentMode === "view" ? flowDisplay : "none",
                      arrowSize,
                      edgeMetric,
                      metricRange,
                      index,
                    });
                  })}
              </LayerGroup>
            )}
            {(!preview || preview.node) && (
              <LayerGroup>
                {(filter === null || filter.node) &&
                  renderedElements.map((element, index) => {
                    if (element.type !== "node") return;
                    return createElement(ElementTypes[element.type], {
                      element: element,
                      key: element.id,
                      currentMode,
                      mapElements: renderedElements,
                      setMapElements: isResultDisplayed
                        ? () => {}
                        : handleEditElements,
                      selectedElement,
                      onSelect: handleMapElementClick,
                      onProjectAreaCheck: handleProjectAreaCheck,
                      index,
                      isInSelection: checkIfInSelection(element),
                      isDisconnected: checkIfDisconnected(element),
                      isOverDepth: checkIfOverDepth(element),
                      showElevation,
                      elevationRange,
                      elevationMetric: effectiveElevationMetric,
                      profileActive,
                    });
                  })}
              </LayerGroup>
            )}
          </>
        )}
        {showElevation && elevationRange && (
          <ElevationLegend
            range={elevationRange}
            metric={effectiveElevationMetric}
          />
        )}
        {currentMode === "view" && metricRange && (
          <MetricLegend range={metricRange} metric={edgeMetric} />
        )}
        {currentMode === "view" && showMetrics && (
          <ResultSummaryPanel
            elements={displayedElements}
            selectedElement={selectedElement}
          />
        )}
      </MapContainer>
      {profileData && (
        <ElevationProfileDialog
          data={profileData}
          onClose={exitElevationProfile}
        />
      )}
      <Alert alert={alert} setAlert={setAlert} />
      <ExoAlert
        open={Boolean(pendingGroupDelete)}
        onClose={() => setPendingGroupDelete(null)}
        onSubmit={confirmGroupDelete}
        alert={{
          type: "warning",
          message: t("Delete {{n}} selected element(s)?", {
            n: pendingGroupDelete ? pendingGroupDelete.length : 0,
          }),
          submitLabel: t("Delete"),
          cancleLabel: t("Cancle"),
        }}
      />
      <Dialog
        open={Boolean(simplifySelection)}
        onClose={cancelSimplify}
        fullWidth={true}
        maxWidth="lg"
        sx={{ zIndex: 99999 }}
      >
        <DialogTitle>{t("Simplify Network")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("Choose the degree of simplification")}
          </DialogContentText>
          {!simplifyPreviews ? (
            <Box className="flex flex-col items-center gap-3 py-8">
              <CircularProgress />
              <Typography>{t("Calculating previews...")}</Typography>
            </Box>
          ) : (
            <Box className="flex flex-wrap justify-center gap-3">
              {simplifyDegrees.map((item) => {
                const variant = simplifyPreviews[item.degree] || [];
                const nodesBefore = mapElements.filter(
                  (el) => el.type === "node"
                ).length;
                const nodesAfter = variant.filter(
                  (el) => el.type === "node"
                ).length;
                return (
                  <Box key={item.degree} sx={{ position: "relative" }}>
                  <Button
                    onClick={() => handleSimplifyConfirm(item.degree)}
                    variant="outlined"
                    color="primary"
                    className="flex flex-col items-center gap-1"
                    sx={{
                      textTransform: "none",
                      width: "340px",
                      alignItems: "center",
                      textAlign: "center",
                      padding: "12px",
                    }}
                  >
                    <SimplifyPreview
                      before={mapElements}
                      after={variant}
                      selection={simplifySelection}
                      width={310}
                      height={230}
                    />
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        color:
                          nodesBefore - nodesAfter > 0 ? "#1976d2" : "inherit",
                        opacity: nodesBefore - nodesAfter > 0 ? 1 : 0.7,
                      }}
                    >
                      {nodesBefore - nodesAfter > 0
                        ? t("{{n}} node(s) will be removed", {
                            n: nodesBefore - nodesAfter,
                          })
                        : t("No nodes will be removed")}
                    </Typography>
                    <Typography fontWeight="bold">{item.label}</Typography>
                    <Typography variant="body2">
                      {t("Nodes")}: {nodesBefore} → {nodesAfter}
                    </Typography>
                    <Typography variant="body2">
                      {t("Total length")}:{" "}
                      {formatLength(totalEdgeLength(mapElements))} →{" "}
                      {formatLength(totalEdgeLength(variant))}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {item.description}
                    </Typography>
                  </Button>
                  {/* magnifier: opens this variant's preview enlarged; sits
                      outside the card button (no nested buttons) */}
                  <Tooltip title={t("Enlarge preview")} placement="top">
                    <IconButton
                      size="small"
                      onClick={() => setEnlargedDegree(item.degree)}
                      sx={{
                        position: "absolute",
                        top: "18px",
                        right: "18px",
                        bgcolor: "rgba(255,255,255,0.85)",
                        color: "#1976d2",
                        ":hover": { bgcolor: "rgba(255,255,255,1)" },
                        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelSimplify} color="primary">
            {t("Cancle")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* enlarged preview of a single variant (magnifier click) */}
      {enlargedDegree != null && simplifyPreviews && simplifySelection && (
        <Dialog
          open
          onClose={() => setEnlargedDegree(null)}
          maxWidth="xl"
          sx={{ zIndex: 99999 }}
        >
          {(() => {
            const item = simplifyDegrees.find(
              (d) => d.degree === enlargedDegree
            );
            const variant = simplifyPreviews[enlargedDegree] || [];
            const nodesBefore = mapElements.filter(
              (el) => el.type === "node"
            ).length;
            const nodesAfter = variant.filter(
              (el) => el.type === "node"
            ).length;
            const removed = nodesBefore - nodesAfter;
            const previewWidth = Math.min(
              Math.round(window.innerWidth * 0.75),
              1100
            );
            const previewHeight = Math.min(
              Math.round(window.innerHeight * 0.62),
              700
            );
            return (
              <>
                <DialogTitle>
                  {t("Simplify Network")} – {item && item.label}
                </DialogTitle>
                <DialogContent>
                  <SimplifyPreview
                    before={mapElements}
                    after={variant}
                    selection={simplifySelection}
                    width={previewWidth}
                    height={previewHeight}
                  />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      mt: 1,
                      color: removed > 0 ? "#1976d2" : "inherit",
                      opacity: removed > 0 ? 1 : 0.7,
                    }}
                  >
                    {removed > 0
                      ? t("{{n}} node(s) will be removed", { n: removed })
                      : t("No nodes will be removed")}{" "}
                    ({t("Nodes")}: {nodesBefore} → {nodesAfter},{" "}
                    {t("Total length")}:{" "}
                    {formatLength(totalEdgeLength(mapElements))} →{" "}
                    {formatLength(totalEdgeLength(variant))})
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => handleSimplifyConfirm(enlargedDegree)}
                    color="primary"
                    variant="contained"
                  >
                    {t("Apply")}
                  </Button>
                  <Button
                    onClick={() => setEnlargedDegree(null)}
                    color="primary"
                  >
                    {t("Close")}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>
      )}
      <ExoAlert
        open={confirmDeleteDisconnected}
        onClose={() => setConfirmDeleteDisconnected(false)}
        onSubmit={deleteDisconnectedElements}
        alert={{
          type: "warning",
          message: t(
            "Delete all elements that are not connected to the network?"
          ),
          submitLabel: t("Delete"),
          cancleLabel: t("Cancle"),
        }}
      />
    </Box>
  );
};

export default MapGridDesigner;
